import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { timestampToIso } from './customersService'
import { stripUndefined } from '../utils/firestore'
import type { Quote } from '../types/quote'

export const quotesRef = collection(db, 'quotes')

/** Tæller der sikrer unikke tilbudsnumre — numre genbruges aldrig. */
const counterDoc = doc(db, 'counters', 'quotes')

function toQuote(snapshot: QueryDocumentSnapshot<DocumentData>): Quote {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    quoteNumber: data.quoteNumber ?? '',
    customerId: data.customerId ?? '',
    customerName: data.customerName ?? '',
    customerEmail: data.customerEmail ?? '',
    customerPhone: data.customerPhone ?? '',
    customerAddress: data.customerAddress ?? '',
    enquiryId: data.enquiryId ?? undefined,
    caseId: data.caseId ?? undefined,
    title: data.title ?? '',
    description: data.description ?? '',
    lines: Array.isArray(data.lines) ? data.lines : [],
    subtotal: Number(data.subtotal) || 0,
    vatAmount: Number(data.vatAmount) || 0,
    total: Number(data.total) || 0,
    status: data.status ?? 'Kladde',
    quoteDate: data.quoteDate ?? '',
    validUntil: data.validUntil ?? '',
    internalNotes: data.internalNotes ?? '',
    customerNotes: data.customerNotes ?? '',
    createdAt: timestampToIso(data.createdAt),
    updatedAt: timestampToIso(data.updatedAt),
    sentAt: timestampToIso(data.sentAt) || undefined,
    acceptedAt: timestampToIso(data.acceptedAt) || undefined,
    rejectedAt: timestampToIso(data.rejectedAt) || undefined,
    emailStatus: data.emailStatus ?? 'ikke sendt',
    emailSentAt: timestampToIso(data.emailSentAt) || undefined,
    dineroInvoiceId: data.dineroInvoiceId ?? undefined,
  }
}

export function subscribeQuotes(
  onChange: (quotes: Quote[]) => void,
  onError: (error: unknown) => void,
): () => void {
  return onSnapshot(
    query(quotesRef, orderBy('createdAt', 'desc')),
    (snapshot) => onChange(snapshot.docs.map(toQuote)),
    onError,
  )
}

/**
 * Reserverer det næste tilbudsnummer i en transaktion, fx TIL-2026-0001.
 * Tælleren nulstilles ved årsskifte og genbruger aldrig et brugt nummer.
 */
export async function reserveQuoteNumber(): Promise<string> {
  const year = new Date().getFullYear()
  const next = await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(counterDoc)
    const data = snapshot.exists() ? snapshot.data() : undefined
    const sameYear = data?.year === year
    const value = sameYear ? Number(data?.lastNumber ?? 0) + 1 : 1
    transaction.set(counterDoc, { year, lastNumber: value, updatedAt: serverTimestamp() })
    return value
  })
  return `TIL-${year}-${String(next).padStart(4, '0')}`
}

export async function createQuoteDoc(
  data: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const created = await addDoc(quotesRef, {
    ...stripUndefined(data as unknown as Record<string, unknown>),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return created.id
}

export async function updateQuoteDoc(id: string, patch: Partial<Quote>): Promise<void> {
  await updateDoc(doc(db, 'quotes', id), {
    ...stripUndefined(patch as Record<string, unknown>),
    updatedAt: serverTimestamp(),
  })
}

export async function deleteQuoteDoc(id: string): Promise<void> {
  await deleteDoc(doc(db, 'quotes', id))
}
